import React, { useEffect } from 'react';
import {
  InputText,
  StylovyzeDialog,
  StylovyzeFormV2,
  useGlobalization,
} from '@innovyze/stylovyze';
import { FormikHelpers, FormikValues } from 'formik';
import { useDispatch } from 'react-redux';
import { useCostTableNames } from '@Selectors';
import { listCostTablesAction } from '@Actions';
import { useCostTablesOverview } from '../../hooks/useCostTableSelectors';
import { COST_TABLES_REQUEST } from '../../utils/costTableRequestParams';
import { normaliseCostTableName } from '../../utils/normaliseCostTableName';

interface RenameCostTableDialogProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  onRename: (newName: string) => Promise<void>;
  modelName: string;
}

export const RenameCostTableDialog = ({
  isOpen,
  setIsOpen,
  onRename,
  modelName,
}: RenameCostTableDialogProps) => {
  const { t } = useGlobalization();
  const dispatch = useDispatch();
  const { successful, isLoading } = useCostTablesOverview();
  const existingNames: Set<string> = useCostTableNames();

  // Intentionally run once on mount. If the cost table list hasn't been loaded
  // yet (e.g. navigating directly to a detail page), fetch it so duplicate-name
  // validation has data to work with. The guard prevents re-fetching if already loaded.
  useEffect(() => {
    if (successful === null && !isLoading) {
      dispatch(listCostTablesAction(COST_TABLES_REQUEST));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fires once on mount to load cost tables if not already loaded; adding deps would re-fetch on every status change
  }, []);

  const handleSubmit = async (
    values: FormikValues,
    { setSubmitting, setFieldError }: FormikHelpers<FormikValues>
  ) => {
    setSubmitting(true);
    try {
      await onRename(values.modelName.trim());
    } catch (error) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      if (
        (error as any)?.response?.data?.error === 'Duplicate cost table name'
      ) {
        setFieldError(
          'modelName',
          t('A cost table with this name already exists')
        );
      } else {
        // Non-duplicate errors are surfaced by the onRename prop via a toast
        // before re-throwing.
      }
      setSubmitting(false);
    }
  };

  return (
    <StylovyzeFormV2
      initialValues={{ modelName: modelName }}
      validate={values => {
        const errors: { [key: string]: string } = {};
        const normalised = normaliseCostTableName(values.modelName);
        if (!values.modelName.length) {
          errors.modelName = t('Required');
        } else if (
          existingNames.has(normalised) &&
          normalised !== normaliseCostTableName(modelName)
        ) {
          errors.modelName = t('A cost table with this name already exists');
        }
        return errors;
      }}
      onSubmit={handleSubmit}>
      {({ values, submitForm, isSubmitting, errors }) => {
        return (
          <StylovyzeDialog
            title={t('Rename Cost Table')}
            open={isOpen}
            confirmText={t('OK')}
            confirmButtonProps={{
              disabled: isSubmitting || Object.keys(errors).length > 0,
            }}
            dialogProps={{
              maxWidth: 'xs',
              fullWidth: true,
              PaperProps: { sx: { minHeight: '19rem' } },
            }}
            onConfirm={submitForm}
            cancelText={t('Cancel')}
            onCancel={() => setIsOpen(false)}>
            <InputText
              fieldAttrs={{ name: 'modelName' }}
              textField={{
                label: t('New Cost Table Name'),
                fullWidth: true,
                error: !!errors.modelName,
                helperText: errors.modelName ?? '',
                value: values.modelName,
              }}
            />
          </StylovyzeDialog>
        );
      }}
    </StylovyzeFormV2>
  );
};

export default RenameCostTableDialog;
